/**
 * Централизованная система мониторинга ошибок
 *
 * В production рекомендуется интеграция с Sentry:
 * npm install @sentry/react
 */

class ErrorMonitoring {
    constructor() {
        this.isProduction = process.env.NODE_ENV === 'production';
        this.errorLog = [];
        this.maxLogSize = 100;

        this.initializeMonitoring();
    }

    initializeMonitoring() {
        // Global error handler
        window.addEventListener('error', (event) => {
            this.logError({
                type: 'global_error',
                message: event.message,
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                error: event.error
            });
        });

        // Unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            this.logError({
                type: 'unhandled_rejection',
                reason: event.reason,
                promise: event.promise
            });
        });
    }

    /**
     * Логирование ошибок
     */
    logError(errorData) {
        const enrichedError = {
            ...errorData,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            userAgent: navigator.userAgent
        };

        // В development - показываем в консоли
        if (!this.isProduction) {
            console.error('🔴 Error captured:', enrichedError);
        }

        // Сохраняем в локальный лог
        this.errorLog.push(enrichedError);
        if (this.errorLog.length > this.maxLogSize) {
            this.errorLog.shift();
        }

        // В production - отправляем на сервер
        if (this.isProduction) {
            this.sendToBackend(enrichedError);
        }

        // Сохраняем в localStorage для debugging
        this.saveToLocalStorage(enrichedError);
    }

    /**
     * Отправка ошибки на backend
     */
    async sendToBackend(errorData) {
        try {
            // TODO: Заменить на ваш endpoint для логирования
            await fetch(`${process.env.REACT_APP_API_URL}/api/logs/error`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(errorData)
            });
        } catch (e) {
            // Тихо игнорируем ошибки отправки логов
            console.warn('Failed to send error log:', e);
        }
    }

    /**
     * Сохранение в localStorage для debugging
     */
    saveToLocalStorage(errorData) {
        try {
            const errors = JSON.parse(localStorage.getItem('app_errors') || '[]');
            errors.push(errorData);

            // Храним только последние 50 ошибок
            if (errors.length > 50) {
                errors.splice(0, errors.length - 50);
            }

            localStorage.setItem('app_errors', JSON.stringify(errors));
        } catch (e) {
            // Игнорируем ошибки localStorage
        }
    }

    /**
     * Получение всех ошибок (для debugging)
     */
    getErrors() {
        try {
            return JSON.parse(localStorage.getItem('app_errors') || '[]');
        } catch {
            return [];
        }
    }

    /**
     * Очистка логов ошибок
     */
    clearErrors() {
        this.errorLog = [];
        localStorage.removeItem('app_errors');
    }

    /**
     * Ручное логирование ошибки
     */
    captureException(error, context = {}) {
        this.logError({
            type: 'manual_capture',
            message: error.message,
            stack: error.stack,
            context
        });
    }

    /**
     * Breadcrumb для отслеживания действий пользователя
     */
    addBreadcrumb(message, data = {}) {
        if (!this.isProduction) {
            console.log('🍞 Breadcrumb:', message, data);
        }

        // В production можно отправлять в Sentry или свой backend
    }
}

// Singleton instance
const errorMonitoring = new ErrorMonitoring();

export default errorMonitoring;

/**
 * Хук для использования в React компонентах
 */
export const useErrorMonitoring = () => {
    return {
        captureException: errorMonitoring.captureException.bind(errorMonitoring),
        addBreadcrumb: errorMonitoring.addBreadcrumb.bind(errorMonitoring)
    };
};